import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService }                    from '@ngx-translate/core';
import { UIRouter }                            from '@uirouter/core';
import { Subscription }                        from 'rxjs';

import { ApiService }                          from '../api/api.service';
import { CodeTypeModel }                       from '../core/models/code-type';
import { EventBusService }                     from '../core/services/event-bus.service';
import { LinkComponent }                       from '../table/cell-components/actions/link.component';
import { SessionService }                      from '../sessions/session.service';
import { Table }                               from '../table/types/table';
import { TableQueryParameters }                from '../table/types/table-query-parameters';
import { TableService }                        from '../table/table.service';
import { TableSwitchControlComponent }         from '../table/cell-components/controls/switch-control/table-switch-control.component';
import { TransactionCenterFiltersModel }       from './models/transaction-center.filters';
import { TransactionCenterVendorInvoiceModel } from './models/transaction-center.vendor-invoice';

@Component({
  selector   : 'wor-tc-vendor-invoices',
  template: require('./transaction-center.vendor-invoices.component.html')
})
export class TransactionCenterVendorInvoicesComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() invoices: Array<TransactionCenterVendorInvoiceModel>;
  @Input() statuses: Array<CodeTypeModel>;

  filters: TransactionCenterFiltersModel;
  tableSettings: Table;

  private tableCacheKey: string;
  private subscription: Subscription;

  constructor (
    private apiService: ApiService,
    private eventBusService: EventBusService,
    private sessionService: SessionService,
    private tableService: TableService,
    private translateService: TranslateService,
    private uiRouter: UIRouter
  ) {}

  ngOnInit (): void {
    this.tableCacheKey = this.uiRouter.stateService.current.data.tableCacheKey;

    this.filters = this.tableService.setFiltersFromCache(this.tableCacheKey, {
      endDate  : '*',
      startDate: '*',
      status   : 'I',
      value    : ''
    });

    const currencySettings = this.sessionService.currency();

    this.tableSettings = {
      broadcast: 'filterVendorInvoices',
      cache: { enabled: true, key: this.tableCacheKey },
      columns: [
        { field: 'post_date', header: { text: this.translateService.instant('GENERAL_SPACE.FIELD.POST_DATE') }, pipe: 'globalDate' },
        { field: 'vendor_invoice_number', header: { text: this.translateService.instant('GENERAL_SPACE.FIELD.REFERENCE') } },
        {
          component: {
            bindings: { inputs: { model: 'vendor_company_name' }, outputs: { onClick: event => this.viewVendor(event) } },
            enabled: true, factory: LinkComponent
          },
          field: 'vendor_company_name',
          header: { text: this.translateService.instant('VENDOR_SPACE.SUPPLIER') },
          sortable: true
        },
        { field: 'invoice_type', header: { text: this.translateService.instant('GENERAL_SPACE.FIELD.INVOICE_TYPE') }, pipe: 'invoiceType' },
        { field: 'vendor_invoice_payment_terms', header: { text: this.translateService.instant('GENERAL_SPACE.FIELD.PAYMENT_TERMS') } },
        {
          classes: ['text-center'],
          component: { bindings: { inputs: { disabled: true, model: 'price_includes_tax' } }, enabled: true, factory: TableSwitchControlComponent },
          field: 'price_includes_tax',
          header: { classes: 'text-center', text: this.translateService.instant('GENERAL_SPACE.FIELD.PRICE_INCLUDES_TAX') },
          sortable: false
        },
        { classes: 'text-right', field: 'gst', header: { classes: 'text-right', text: currencySettings.tax_name }, pipe: 'globalCurrency' },
        { classes: 'text-right', field: 'total', header: { classes: 'text-right', text: this.translateService.instant('GENERAL_SPACE.FIELD.TOTAL') }, pipe: 'globalCurrency' }
      ],
      empty: { text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_INVOICES_FOUND') },
      pagination: {
        enabled: true,
        pageSize: this.tableService.getCachedPaging(this.tableCacheKey).pageSize || 10,
        pageSizes: [10, 25, 50],
        queryCount: () => this.getCount()
      },
      rows: { highlight: { enabled: true } },
      sorting: this.tableService.setSortingFromCache(this.tableCacheKey, { direction: 'desc', field: 'post_date' })
    };

    this.subscription = this.eventBusService.on<string>('searchFilterChanged').subscribe(value => {
      if (!this.uiRouter.stateService.transition) {

        this.filters.value = value;

        this.onFiltersChanged();

      }
    });
  }

  ngAfterViewInit (): void {
    const interval = setInterval(() => {
      if (typeof window['filterVendorInvoices'] === 'function') {

        clearInterval(interval);

        this.onFiltersChanged();

      }
    }, 100);
  }

  ngOnDestroy (): void {


    this.subscription?.unsubscribe();


  }

  onFiltersChanged (): void {
    if (window['filterVendorInvoices']) {

      window['filterVendorInvoices'](this.filters);

      };

    }

  get startDateDisplay(): string {


    return this.filters.startDate === '*' ? '' : this.filters.startDate;


  }
  set startDateDisplay(value: string) {

    this.filters.startDate = value || '*';

    this.onFiltersChanged();

  }

  get endDateDisplay(): string {

    return this.filters.endDate === '*' ? '' : this.filters.endDate;

  }
  set endDateDisplay(value: string) {

    this.filters.endDate = value || '*';

    this.onFiltersChanged();

  }

  getInvoices(params: TableQueryParameters): Promise<Array<TransactionCenterVendorInvoiceModel>> {
    return this.apiService.get('/transaction_center/supplier_invoices', [
      this.filters.startDate || '*',
      this.filters.endDate   || '*',
      this.filters.status    || 'I',
      this.filters.value     || '*',
      params.field,
      params.direction,
      params.pageSize,
      params.offset
    ]).then((invoices: Array<TransactionCenterVendorInvoiceModel>) => {
      this.invoices = invoices;
      return invoices;
    });
  }

  getCount (): Promise<number> {


    return this.apiService.get('/transaction_center/supplier_invoices_count', [
      this.filters.startDate || '*',
      this.filters.endDate   || '*',
      this.filters.status    || 'I',
      this.filters.value     || '*'
    ]).then((count) => count.vendor_invoices_count);


  }

  viewInvoice(invoice: TransactionCenterVendorInvoiceModel): void {


    this.uiRouter.stateService.go('app.vendor-invoice', { id: invoice.id, vendor_id: invoice.vendor_id });


  }

  viewVendor(invoice: TransactionCenterVendorInvoiceModel): void {


    this.uiRouter.stateService.go('app.vendor', { id: invoice.vendor_id });


    };


  }
